import {
  MessageProps,
  MessagesTaskDetailsProps
} from 'contexts/MessagesContext/messagesContext.types';
import { MessageType } from 'enums/messages';
import SocketFrontDesk from 'socket/socketFrontDesk';
import SocketMessages from 'socket/socketMessages';
import { FileItemProps, SendMessageProps } from 'store/chat/chat.types';
import { getMessageType } from 'utils/helpers';

import { apiSlice } from '../api/apiSlice';
import { addMessage, markSeen } from './chatSlice';

export const chatApi = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    deleteMessage: build.mutation({
      invalidatesTags: [{ id: 'LIST', type: 'Messages' }],
      query: ({ messageId, type }) => {
        const messagesType = getMessageType(type);

        const params = {
          method: 'DELETE',
          url: `/messages/${messageId}?type=${messagesType}`
        };

        return params;
      }
    }),
    getCareMessages: build.query<
      {
        chatTaskDetails: MessagesTaskDetailsProps;
        messages: MessageProps[];
        nextKey: string;
        totalCount: number;
      },
      {
        channelId: string;
        limit?: number;
        nextKey?: string;
        pageNo?: number;
        sortOrder?: string;
        type: MessageType;
      }
    >({
      keepUnusedDataFor: 0,

      async onCacheEntryAdded(arg, { cacheDataLoaded, cacheEntryRemoved, dispatch }) {
        const socket = arg.type === MessageType.Medical ? SocketMessages : SocketFrontDesk;

        // when data is received from the socket connection to the server,
        // update our query result with the received message
        const messageToClientsListener = (newMessage: MessageProps) => {
          // Insert received message from the websocket
          // into the existing RTKQ cache array
          if (newMessage && newMessage.channelId === arg.channelId) {
            // Only add the message to the current channel's messages
            dispatch(addMessage(newMessage));
            // Note: Channel update and RespondState invalidation are handled in MessagesContext
            // to avoid duplicate updates
          }
        };

        const markedSeenListener = (seenMessage: MessageProps) => {
          if (seenMessage) {
            if (seenMessage.channelId === arg.channelId) dispatch(markSeen(seenMessage));
          }
        };

        try {
          // wait for the initial query to resolve before proceeding
          await cacheDataLoaded;

          socket.on('messageToClients', messageToClientsListener);
          socket.on('markedSeen', markedSeenListener);
        } catch {
          // no-op in case `cacheEntryRemoved` resolves before `cacheDataLoaded`,
          // in which case `cacheDataLoaded` will throw
        }
        // cacheEntryRemoved will resolve when the cache subscription is no longer active
        await cacheEntryRemoved;
        // perform cleanup steps once the `cacheEntryRemoved` promise resolve ex. https://redux-toolkit.js.org/rtk-query/usage/streaming-updates#streaming-update-examples
        socket.off('messageToClients', messageToClientsListener);
        socket.off('markedSeen', markedSeenListener);
      },
      providesTags: (result) => {
        return result
          ? [
              { id: 'LIST', type: 'Messages' },
              ...result.messages.map(({ _id }) => ({ id: _id, type: 'Messages' as const }))
            ]
          : ['Messages'];
      },
      query: ({ channelId, limit = 10, nextKey, pageNo = 0, sortOrder, type }) => {
        // in case of uploading the gap messages, when sortBy: asc, the nextKey
        // should be base64 encoded string of the last freshest message {_id, createdAt}
        // limit should be 10
        const messagesType = type === MessageType.Medical ? 'care-messages' : 'front-desk-messages';
        return {
          params: {
            limit,
            pageNo,
            ...(nextKey && { nextKey }), // pass nextKey only if it is defined
            ...(sortOrder && { sortOrder }) // pass sortOrder only if it is defined
          },
          url: `channels/${channelId}/${messagesType}`
        };
      },
      transformResponse: (response: {
        data: { messages: MessageProps[]; taskDetails: MessagesTaskDetailsProps };
        info: { nextKey: string; totalCount: number };
      }) => {
        return {
          chatTaskDetails: response.data.taskDetails,
          messages: response.data.messages.map((message) => ({ ...message, isBlurred: true })),
          nextKey: response.info.nextKey,
          totalCount: response.info.totalCount
        };
      }
    }),
    sendMessage: build.mutation<null, SendMessageProps>({
      invalidatesTags: [{ id: 'LIST', type: 'Messages' }, 'RespondState'],
      query: ({ channelId, fileName, filePath, message, patientId, type }: SendMessageProps) => {
        const messagesType = getMessageType(type);
        return {
          body: {
            channelId,
            fileName,
            filePath,
            message,
            patientUserId: patientId,
            type: messagesType
          },
          method: 'POST',
          url: `/messages`
        };
      }
    }),
    updateMessage: build.mutation({
      invalidatesTags: [{ id: 'LIST', type: 'Messages' }],
      query: ({ message, messageId, type }) => {
        const messagesType = getMessageType(type);

        const params = {
          body: { message },
          method: 'PATCH',
          url: `/messages/${messageId}?type=${messagesType}`
        };

        return params;
      }
    }),
    uploadFile: build.mutation({
      query: (file) => {
        const formData = new FormData();
        formData.append('chatFile', file);
        return {
          body: formData,
          headers: {
            'API-KEY': import.meta.env.VITE_API_KEY
          },
          method: 'POST',
          url: '/channels/upload-file'
        };
      },
      transformResponse: (response: { data: FileItemProps }) => response.data
    })
  })
});

export const {
  useDeleteMessageMutation,
  useLazyGetCareMessagesQuery,
  useSendMessageMutation,
  useUpdateMessageMutation,
  useUploadFileMutation
} = chatApi;
